/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import {
  HallowAnalyticsEvent,
  useAnalytics,
  useViewedScreen,
} from "@packages/analytics";
import type { ThemeType } from "@packages/ui";
import { useCallback, useEffect, useMemo } from "react";

import { ScreenNames } from "../../lib/enums/screenNames";
import { Screens } from "../../lib/enums/screens";
import { useContext as useContextProvider } from "../../Provider";
import { useRequestMePinExists } from "../../requests/useRequestMePinExists";
import { useRequestMePinForgotPost } from "../../requests/useRequestMePinForgotPost";
import { Step, useContext } from "./Creation";
import { CreationPinForm } from "./CreationPinForm";

const cssDivButtonsContainer: Interpolation<ThemeType> = (theme) => ({
  display: "flex",
  flexDirection: "column-reverse",
  gap: theme.spacing.s,
  [`@media ${Screens.Large}`]: {
    flexDirection: "row-reverse",
    justifyContent: "space-between",
    paddingBottom: theme.spacing.xxl,
  },
});
const cssDivSpacer: Interpolation<ThemeType> = (theme) => ({
  height: theme.spacing.l,
  [`@media ${Screens.Large}`]: {
    flexGrow: 1,
  },
});
const cssDivTextContainer: Interpolation<ThemeType> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.s,
});
const cssLabel: Interpolation<ThemeType> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.l,
  paddingBottom: theme.spacing.s,
  paddingTop: theme.spacing.s,
});
const cssTextDescription: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigher,
  margin: 0,
});
const cssTextTitle = {
  margin: 0,
};

export const CreationPin = ({ pinError }: { pinError?: string }) => {
  const { track } = useAnalytics();
  const { form, step } = useContext();
  const { setToast } = useContextProvider();
  const query = useRequestMePinExists();
  const mutationPost = useRequestMePinForgotPost();

  const pin = form.watch("pin");

  const isPinRepeating = useMemo(() => {
    return new Set(String(pin)).size === 1;
  }, [pin]);
  const screenName = useMemo(() => {
    return query.data?.exists
      ? ScreenNames.ParentPinInput
      : ScreenNames.KidsProfileCreationParentPin;
  }, [query.data?.exists]);
  useViewedScreen({ isReady: step === Step.Pin, screen: screenName });

  const onClick = useCallback(async () => {
    track({
      event: HallowAnalyticsEvent.TappedForgotParentPin,
      properties: { screen_name: screenName },
    });
    const response = await mutationPost.mutateAsync({});
    if (response.success_text) setToast(response.success_text);
  }, [mutationPost.mutateAsync, screenName, setToast, track]);

  useEffect(() => {
    if (!query.data?.exists && pinError)
      track({
        event: HallowAnalyticsEvent.EnteredIncorrectParentPIN,
        properties: {
          error: isPinRepeating
            ? "four_identical_numbers"
            : "four_consecutive_numbers",
          screen_name: screenName,
        },
      });
  }, [pinError, query.data?.exists]);

  useEffect(() => {
    if (step !== Step.Pin) return;

    if (typeof window !== "undefined")
      document.getElementById("pin-0")?.focus();
  }, [step]);

  if (step !== Step.Pin) return null;

  return (
    <CreationPinForm form={form} onForgotPin={onClick} pinError={pinError} />
  );
};
